// src/features/tag-types/components/TagTypeCreate.tsx
import React from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { MyToolbar } from '../../../components/forms/MyToolbar';
import { MyFormContainer } from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection 
} from '../../../components/forms/MyFormLayout';

import { prepareTagTypeForAPI, VALID_TYPE_CODES } from '../types';
import { useTagTypeValidation } from '../hooks/useTagTypeValidation';

export const TagTypeCreate: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const { validateTypeCode, validateName, validateDescription } = useTagTypeValidation();

  const transform = (data: any) => prepareTagTypeForAPI(data);

  const handleSuccess = () => {
    notify(translate('resources.tag-types.notifications.created', 'Tipo de Tag creado con éxito'), { type: 'success' });
    redirect('/tag-types');
  };
  
  const handleError = (error: any) => {
    console.error('Error creating tag type:', error);
    notify(error.message || translate('resources.tag-types.errors.create', 'Error al crear el tipo de tag'), { type: 'error' });
  };
  
  const handleCancel = () => redirect('/tag-types');

  return (
    <Create
      title={translate('resources.tag-types.titles.create', 'Crear Tipo de Tag')}
      transform={transform}
      mutationOptions={{ onSuccess: handleSuccess, onError: handleError }}
      redirect={false}
    >
      <MyFormContainer maxWidth="lg">
        <SimpleForm
          toolbar={
            <MyToolbar 
              onCancel={handleCancel} 
              resource="tag-types" 
              showDelete={false} 
            />
          }
        >
          <CenteredFormLayout>
            <MyFormSection
              title={translate('resources.tag-types.sections.new_title', 'Nuevo Tipo de Tag')}
              subtitle={translate('resources.tag-types.sections.new_subtitle', 'Complete los datos para crear un nuevo tipo de tag')}
              icon={<AddIcon />}
              variant="card"
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Inputs en la misma fila para pantallas medianas y grandes */}
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <SelectInput
                    source="typeCode"
                    label={translate('resources.tag-types.fields.typeCode', 'Type Code')}
                    choices={VALID_TYPE_CODES}
                    validate={[required()]}
                    fullWidth
                    helperText="Selecciona el tipo de identificador"
                  />
                  <TextInput
                    source="name"
                    label={translate('resources.tag-types.fields.name', 'Display Name')}
                    validate={[required(), validateName]}
                    fullWidth
                    variant="outlined"
                    helperText={translate('resources.tag-types.help.name', 'Nombre legible para el usuario')}
                  />
                </Box>
                
                <TextInput
                  source="description"
                  label={translate('resources.tag-types.fields.description', 'Descripción')}
                  multiline
                  rows={3}
                  validate={validateDescription}
                  fullWidth
                  variant="outlined"
                  helperText={translate('resources.tag-types.help.description', 'Descripción opcional (máx. 500 caracteres)')}
                />
                
                {/* Configuración de comportamiento */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Configuración de Comportamiento
                  </Typography>
                  
                  <BooleanInput
                    source="isSystemGenerated"
                    label="Generado por Sistema"
                    helperText="Permite que el sistema genere códigos automáticamente para este tipo"
                    defaultValue={false}
                  />
                  
                  <BooleanInput
                    source="requiresManualInput"
                    label="Requiere Entrada Manual"
                    helperText="El usuario debe introducir manualmente el código (ej. NFC UID)"
                    defaultValue={true}
                  />
                </Box>
                
              </Box>
            </MyFormSection>
          </CenteredFormLayout>
        </SimpleForm>
      </MyFormContainer>
    </Create>
  );
};

export default TagTypeCreate;